import { motion } from "framer-motion";
import { Activity, Radar, Ruler, PersonStanding, ArrowLeftRight, TrendingUp } from "lucide-react";

const items = [
  { num: "01", title: "Body Fat %", desc: "Track your true body fat, not just your weight.", Icon: Activity },
  { num: "02", title: "Visceral Fat Monitored", desc: "Know what's happening inside.", Icon: Radar },
  { num: "03", title: "250+ Measurements", desc: "Detailed. Accurate. Comprehensive.", Icon: Ruler },
  { num: "04", title: "Posture & Symmetry", desc: "See imbalances. Improve alignment.", Icon: PersonStanding },
  { num: "05", title: "Before / After 3D", desc: "Visualize real changes over time.", Icon: ArrowLeftRight },
  { num: "06", title: "Monthly Progress", desc: "Track. Improve. Stay motivated.", Icon: TrendingUp },
];

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export const Benefits = () => (
  <section data-testid="benefits-section" className="px-6 md:px-12 py-24 md:py-32 max-w-7xl mx-auto">
    <motion.header {...fadeUp} transition={{ duration: 0.8 }}>
      <p className="text-xs uppercase tracking-[0.35em] text-volt mb-4">The scan</p>
      <h2 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[0.95]">
        What you'll <span className="text-outline">actually</span> see
      </h2>
    </motion.header>

    <div className="mt-14 mb-16 grid grid-cols-12 gap-4 items-end">
      <motion.figure
        {...fadeUp}
        transition={{ duration: 0.9 }}
        className="col-span-12 md:col-span-7 border border-white/10 overflow-hidden"
      >
        <img
          src="https://images.unsplash.com/photo-1674834727206-4bc272bfd8da?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Premium studio interior"
          data-testid="benefits-image-studio"
          className="h-64 md:h-96 w-full object-cover grayscale-[35%] hover:grayscale-0 transition-[filter] duration-700"
        />
      </motion.figure>
      <motion.figure
        {...fadeUp}
        transition={{ duration: 0.9, delay: 0.15 }}
        className="col-span-8 col-start-5 md:col-span-4 md:col-start-9 -mt-12 md:mt-0 border border-white/10 overflow-hidden shadow-[0_0_50px_rgba(30,111,255,0.12)]"
      >
        <img
          src="https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Athlete posture and form"
          data-testid="benefits-image-athlete"
          className="h-56 md:h-72 w-full object-cover grayscale-[35%] hover:grayscale-0 transition-[filter] duration-700"
        />
      </motion.figure>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="benefits-grid">
      {items.map(({ num, title, desc, Icon }, i) => (
        <motion.div
          key={num}
          {...fadeUp}
          transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
          className="bg-[#050505] p-8 md:p-10 group"
          data-testid={`benefit-chapter-${num}`}
        >
          <span className="font-display text-5xl text-white/10 group-hover:text-volt transition-colors duration-500">{num}</span>
          <Icon className="mt-6 h-6 w-6 text-volt" />
          <h3 className="mt-3 font-display uppercase text-xl tracking-wide">{title}</h3>
          <p className="mt-2 text-sm text-white/55 leading-relaxed">{desc}</p>
        </motion.div>
      ))}
    </div>
  </section>
);
